import { useLayoutEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { V2Spinner } from "./V2BusyFrame";
import { captureCollapseAnchor, settleCollapseScroll } from "./v2CollapseScroll";

export default function V2Accordion({
  id,
  title,
  help,
  badge,
  badgeTone = "neutral",
  open,
  onToggle,
  locked = false,
  loading = false,
  children,
}) {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const pendingCollapseRef = useRef(null);

  const handleToggle = () => {
    if (locked) return;
    if (open) {
      pendingCollapseRef.current = captureCollapseAnchor(headerRef.current);
    }
    onToggle();
  };

  useLayoutEffect(() => {
    const pending = pendingCollapseRef.current;
    if (!pending || open) return;
    pendingCollapseRef.current = null;
    settleCollapseScroll(pending, sectionRef.current);
  }, [open]);

  return (
    <section
      ref={sectionRef}
      className="scroll-mt-2 rounded-[10px] border border-[rgba(98,94,166,0.42)] bg-[rgba(17,12,61,0.72)]"
      data-acc={id}
    >
      <button
        ref={headerRef}
        type="button"
        className="flex min-h-14 w-full items-center justify-between gap-4 border-0 bg-transparent px-[18px] text-left text-inherit"
        onClick={handleToggle}
        aria-expanded={open}
        aria-busy={loading || undefined}
        aria-disabled={locked || undefined}
      >
        <div className="flex flex-col gap-[3px]">
          <strong className="font-display text-[18px] uppercase tracking-[0.03em] text-[#f4f2ff]">
            {title}
          </strong>
          {help && <span className="text-[12px] text-[#8b86bc]">{help}</span>}
        </div>
        <div className="flex items-center gap-2.5">
          {loading ? <V2Spinner size={13} /> : null}
          {badge && (
            <span
              className={`rounded-[14px] border px-[9px] py-1 text-[11px] ${
                badgeTone === "danger"
                  ? "border-[rgba(255,77,94,0.7)] bg-v2-bad-soft text-[#ff6e7d]"
                  : "border-[rgba(98,94,166,0.55)] text-[#aaa5d7]"
              }`}
            >
              {badge}
            </span>
          )}
          {locked ? null : (
            <ChevronDown
              size={16}
              className={`text-v2-subtle transition-transform duration-150 ${open ? "rotate-180" : ""}`}
            />
          )}
        </div>
      </button>
      {open && (
        <div className="overflow-visible px-[18px] pb-[18px]" style={{ overflowAnchor: "none" }}>
          {children}
        </div>
      )}
    </section>
  );
}
